import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  notificationCellsApi,
  notificationSettingsQueryKeys,
  type MessagingTemplate,
  type NotificationCell,
  type NotificationEventRow,
} from "../../shared/api/notificationSettings";
import { servicesApi } from "../../shared/api/services";
import { departmentsApi } from "../../shared/api/departments";
import { apiError } from "../../shared/api/client";
import { templateName } from "./EventChannelTemplates";

const CHANNEL_SHORT: Record<string, string> = { ZBS: "ZBS", SMS: "SMS", EMAIL: "Email" };

/**
 * What one service does differently from the clinic default, in a few words (#19585, #19593):
 * "ZBS: Nhắc lịch hẹn khám · SMS: tắt". Only the differences — a row that repeats the default's
 * template says so, since it stays pinned to that template if the default later changes. Null when
 * the service has written nothing of its own yet.
 */
export function overrideSummary(
  cells: NotificationCell[],
  serviceId: string,
  templates: MessagingTemplate[],
): string | null {
  const parts = cells
    .filter((c) => c.serviceId === serviceId)
    .map((c) => {
      const ch = CHANNEL_SHORT[c.channel] ?? c.channel;
      const base = cells.find((d) => d.serviceId == null && d.channel === c.channel);
      if (!c.enabled) return `${ch}: tắt`;
      if (c.templateCode) {
        const t = templates.find((x) => x.code === c.templateCode);
        const name = templateName(t, c.channel) ?? c.templateCode;
        return c.templateCode === base?.templateCode ? `${ch}: ${name} (giống mặc định)` : `${ch}: ${name}`;
      }
      return null;
    })
    .filter((p): p is string => !!p);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * The clinic default in a few words: "ZBS: Đánh giá dịch vụ". Only the channels switched on for
 * the clinic — one switched off sends nothing whatever its cell says.
 */
export function defaultSummary(
  cells: NotificationCell[],
  templates: MessagingTemplate[],
  channels?: string[],
): string | null {
  const parts = cells
    .filter((c) => c.serviceId == null && (!channels || channels.includes(c.channel)))
    .map((c) => {
      const ch = CHANNEL_SHORT[c.channel] ?? c.channel;
      if (!c.enabled) return `${ch}: tắt`;
      if (!c.templateCode) return null;
      const t = templates.find((x) => x.code === c.templateCode);
      return `${ch}: ${templateName(t, c.channel) ?? c.templateCode}`;
    })
    .filter((p): p is string => !!p);
  return parts.length ? parts.join(" · ") : null;
}

/**
 * "Cấu hình gửi" (#19585, #19593) — which scope the settings below edit: the clinic default, or
 * one service's exception. Every scope is in force at once, so they are rows to open, not radio
 * options: radios read as "send by this one instead of that one" (#19593). Replaces the per-service matrix, whose first row
 * was a second editor for the very cell the pickers above it already edited.
 *
 * <p>Only services that have their own rows are listed: a service with none follows the default,
 * and listing every service would make "configured" and "not configured" look alike — the
 * difference the fallback design rests on (#18933).
 */
export function ScopeBar({
  event,
  templates,
  scope,
  channels,
  onScope,
  onError,
}: {
  /** Channels switched on for the clinic; the default's summary names only these. */
  channels?: string[];
  event: NotificationEventRow;
  templates: MessagingTemplate[];
  /** The service being edited, or null for the clinic default. */
  scope: string | null;
  /** Asked, not set: the page guards an unsaved draft before the scope changes. */
  onScope: (next: string | null) => void;
  onError: (m: string | null) => void;
}) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState("");
  const [filter, setFilter] = useState("");
  /** The service whose removal is being asked about, inline on its own row. */
  const [confirmRemove, setConfirmRemove] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const cellsQ = useQuery({
    queryKey: notificationSettingsQueryKeys.cells(event.eventKey),
    queryFn: () => notificationCellsApi.list(event.eventKey),
  });
  const servicesQ = useQuery({
    queryKey: ["clinic-services-all"],
    queryFn: () => servicesApi.listAll(),
  });
  // Only to name the khoa of a department-scoped service: an override on one applies to visits in
  // that khoa and nowhere else, and the screen has to say so (#18929).
  const departmentsQ = useQuery({
    queryKey: ["clinic-departments-for-notification-scope"],
    queryFn: () => departmentsApi.listAll(),
  });

  const cells = cellsQ.data ?? [];
  const services = servicesQ.data ?? [];
  const configured = [
    ...new Set(cells.filter((c) => c.serviceId).map((c) => c.serviceId as string)),
  ];
  // Only an active service can be a visit's primary service, so only those are offered (#18929).
  const unconfigured = services.filter((s) => s.active && !configured.includes(s.id));

  const departmentName = (id: string | null) =>
    id === null ? null : ((departmentsQ.data ?? []).find((d) => d.id === id)?.name ?? null);
  /** "Khám răng" · "Khám răng (khoa Răng - Hàm - Mặt)" · "Khám răng (ngừng hoạt động)". */
  const serviceLabel = (id: string) => {
    const s = services.find((x) => x.id === id);
    if (!s) return "(dịch vụ đã xoá)";
    const notes = [
      departmentName(s.departmentId) ? `khoa ${departmentName(s.departmentId)}` : null,
      // Kept visible after the service is switched off, so its override can still be removed.
      s.active ? null : "ngừng hoạt động",
    ].filter(Boolean);
    return notes.length ? `${s.name} (${notes.join(", ")})` : s.name;
  };

  const fail = (e: unknown) => {
    setLocalError(apiError(e).detail);
    onError(null);
  };
  const add = useMutation({
    // Seeded from the clinic default rather than from nothing: an override that starts by
    // silently switching Zalo off would be a trap. No template of its own — it follows the default
    // until one is chosen (#19581).
    mutationFn: (serviceId: string) =>
      notificationCellsApi.save(event.eventKey, {
        serviceId,
        channel: "ZBS",
        enabled: cells.find((c) => c.serviceId == null && c.channel === "ZBS")?.enabled ?? true,
        templateCode: null,
        boundParams: null,
      }),
    onSuccess: (_d, serviceId) => {
      qc.invalidateQueries({ queryKey: notificationSettingsQueryKeys.cells(event.eventKey) });
      setAdding("");
      setLocalError(null);
      onScope(serviceId);
    },
    onError: fail,
  });
  const remove = useMutation({
    mutationFn: (serviceId: string) => notificationCellsApi.removeService(event.eventKey, serviceId),
    onSuccess: (_d, serviceId) => {
      qc.invalidateQueries({ queryKey: notificationSettingsQueryKeys.cells(event.eventKey) });
      qc.invalidateQueries({ queryKey: notificationSettingsQueryKeys.events() });
      setConfirmRemove(null);
      setLocalError(null);
      if (scope === serviceId) onScope(null);
    },
    onError: fail,
  });

  const shown = configured.filter(
    (id) => !filter.trim() || serviceLabel(id).toLowerCase().includes(filter.trim().toLowerCase()),
  );
  // Active services with no exception of their own: what "dùng cho N dịch vụ" counts.
  const followingDefault = unconfigured.length;
  const otherExceptions = configured.filter((id) => id !== scope).length;

  /** One scope as a row to open, not an option to pick instead of another (#19593). */
  const row = (id: string | null, label: string, summary: string | null) => {
    const active = scope === id;
    return (
      <div
        key={id ?? "__default__"}
        className={
          "flex min-w-0 items-start gap-2 rounded-md border px-2 py-1.5 text-sm " +
          (active ? "border-primary bg-primary/10" : "border-transparent hover:bg-muted/50")
        }
        data-testid={`scope-row-${id ?? "default"}`}
      >
        <button
          type="button"
          className="grid min-w-0 flex-1 text-left"
          aria-current={active ? "true" : undefined}
          onClick={() => {
            setConfirmRemove(null);
            if (!active) onScope(id);
          }}
        >
          <span className="flex flex-wrap items-center gap-2">
            <span className={active ? "font-medium" : ""}>{label}</span>
            {active && (
              <span className="rounded bg-primary px-1.5 text-[11px] font-medium text-primary-foreground">
                đang sửa
              </span>
            )}
          </span>
          {summary && <span className="text-xs text-muted-foreground">{summary}</span>}
        </button>
        {/* #19581 — the way back to the default, on the exception's own row. Asked inline, not
            with window.confirm: one click must not throw away three channels' choices. */}
        {id &&
          (confirmRemove === id ? (
            <span className="flex flex-wrap items-center justify-end gap-2 text-xs" role="group">
              <span>Xoá ngoại lệ, dùng lại mặc định phòng khám?</span>
              <button
                type="button"
                className="rounded border border-destructive px-1.5 text-destructive disabled:opacity-50"
                disabled={remove.isPending}
                onClick={() => remove.mutate(id)}
              >
                {remove.isPending ? "Đang xoá…" : "Xoá"}
              </button>
              <button
                type="button"
                className="underline"
                disabled={remove.isPending}
                onClick={() => setConfirmRemove(null)}
              >
                Huỷ
              </button>
            </span>
          ) : (
            <button
              type="button"
              className="shrink-0 text-xs text-destructive underline-offset-2 hover:underline"
              aria-label={`Xoá ngoại lệ của ${serviceLabel(id)}`}
              onClick={() => setConfirmRemove(id)}
            >
              Xoá
            </button>
          ))}
      </div>
    );
  };

  // Who a change made here reaches — the question radios made people get wrong (#19593).
  const impact =
    scope === null
      ? `Đang sửa: mặc định phòng khám. Áp dụng cho ${followingDefault} dịch vụ chưa có ngoại lệ` +
        (configured.length
          ? `, và cho các kênh mà ${configured.length} dịch vụ ngoại lệ chưa chỉnh riêng.`
          : ".")
      : `Đang sửa: ngoại lệ của ${serviceLabel(scope)}. Chỉ ca khám dùng dịch vụ này bị ảnh hưởng — ` +
        `${followingDefault} dịch vụ khác vẫn gửi theo mặc định phòng khám` +
        (otherExceptions ? `, ${otherExceptions} dịch vụ theo ngoại lệ của riêng chúng.` : ".");

  return (
    <div className="grid gap-1.5" data-testid="scope-bar">
      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Cấu hình gửi
      </span>
      <div className="grid gap-1 rounded-md border p-1.5">
        {row(
          null,
          "Mặc định phòng khám",
          [
            servicesQ.isLoading ? null : `Dùng cho ${followingDefault} dịch vụ chưa có ngoại lệ`,
            defaultSummary(cells, templates, channels),
          ]
            .filter(Boolean)
            .join(" · "),
        )}
        <div className="grid gap-1 border-t pl-4 pt-1.5" data-testid="scope-exceptions">
          <span className="text-xs font-medium text-muted-foreground">
            Ngoại lệ theo dịch vụ ({configured.length})
          </span>
          {configured.length >= 5 && (
            <input
              className="h-8 w-full max-w-xs rounded-md border bg-background px-2 text-xs"
              placeholder="Lọc dịch vụ…"
              aria-label="Lọc dịch vụ có ngoại lệ"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          )}
          {configured.length === 0 && (
            <small className="px-2 text-xs text-muted-foreground">
              Chưa có. Thêm ngoại lệ khi một dịch vụ cần gửi khác mặc định.
            </small>
          )}
          {shown.map((id) => {
            const diff = overrideSummary(cells, id, templates);
            return row(id, serviceLabel(id), diff ? `Riêng: ${diff}` : "Chưa khác mặc định");
          })}
          {unconfigured.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 px-2">
              <select
                className="h-8 min-w-0 max-w-full rounded-md border bg-background px-2 text-xs"
                value={adding}
                aria-label="Thêm ngoại lệ cho dịch vụ"
                onChange={(e) => setAdding(e.target.value)}
              >
                <option value="">+ Thêm ngoại lệ cho dịch vụ…</option>
                {unconfigured.map((s) => (
                  <option key={s.id} value={s.id}>
                    {serviceLabel(s.id)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="h-8 rounded-md border px-2 text-xs disabled:opacity-50"
                disabled={!adding || add.isPending}
                onClick={() => add.mutate(adding)}
              >
                Thêm
              </button>
            </div>
          )}
        </div>
      </div>
      {!servicesQ.isLoading && !cellsQ.isLoading && (
        <p className="flex gap-1.5 rounded-md bg-muted/60 px-2 py-1.5 text-xs" data-testid="scope-impact">
          <span aria-hidden>ⓘ</span>
          <span>{impact}</span>
        </p>
      )}
      {localError && (
        <p className="text-xs text-destructive" role="alert">
          {localError}
        </p>
      )}
    </div>
  );
}
